import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { BsDatepickerModule, BsDatepickerConfig } from 'ngx-bootstrap/datepicker';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { LocaleStateModel } from './datepicker-form-field.model';
import { HelperService } from 'app/shared/services/helper.service';
import { InputHelperTextComponent } from 'app/shared/components/input-helper-text/input-helper-text.component';

@Component({
  selector: 'app-datepicker-form-field',
  standalone: true,
  imports: [CommonModule, TranslateModule, ReactiveFormsModule, BsDatepickerModule, InputHelperTextComponent],
  templateUrl: './datepicker-form-field.component.html',
  styleUrls: ['./datepicker-form-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatepickerFormFieldComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => DatepickerFormFieldComponent),
      multi: true,
    }
  ],
})
export class DatepickerFormFieldComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() label: string = '';
  @Input() type: string = 'text';
  @Input() placeholder: string = 'placeholders.please_select';
  @Input() isRequired: boolean = false;
  @Input() infoText: string = '';
  @Input() viewMode!: boolean;
  @Input() localeState: LocaleStateModel = new LocaleStateModel();
  @Input() minDate!: Date | undefined;
  @Input() maxDate!: Date | undefined;
  @Input() isLabelTopPosition: boolean = false;
  @Input() helpText!: string;
  @Input() highlightClass!: string;

  formControl: FormControl = new FormControl<string>('');
  errorText: string = '';
  inlineEditMode!: boolean;

  bsConfig: Partial<BsDatepickerConfig> = {};

  private unsubscribe$ = new Subject<void>();

  onTouch: () => void = () => { };
  onChange: (value: any) => void = () => { };

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private helperService: HelperService
  ) { }

  ngOnInit(): void {
    this.bsConfig = { ...this.localeState };

    this.minDate = new Date(this.minDate as Date);
    this.maxDate = new Date(this.maxDate as Date);

    this.formControl.valueChanges
    .pipe(takeUntil(this.unsubscribe$))
    .subscribe((value) => {
      const utcDate = this.helperService.convertToMidnightUTC(value);
      this.onChange(utcDate);
    });
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.formControl.disable() : this.formControl.enable();
  }

  writeValue(value: string): void {
    this.formControl.setValue(value, { emitEvent: false });
    this.changeDetectorRef.markForCheck();
  }

  validate(control: FormControl) {
    setTimeout(() => {
      this.errorText = this.helperService.handleFieldsErrors(control);
      this.changeDetectorRef.markForCheck();
    });
    return null;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}

